import { useFormik } from "formik";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { useToast } from "hooks/useToast";
import { useEffect, useState } from "react";
import * as Yup from "yup";

const validationSchema = Yup.object({
  name: Yup.string().required("Nome é obrigatório").min(2, "Nome muito curto"),
  description: Yup.string().optional(),
  capacity: Yup.number()
    .optional()
    .min(0, "Capacidade não pode ser negativa")
    .integer("Capacidade deve ser inteiro"),
  isActive: Yup.boolean().default(true),
});

const initInputsValues = {
  name: "",
  description: "",
  capacity: "" as unknown as number | "",
  isActive: true,
};

export const useLocationForm = (
  onSuccess?: () => void,
  editingLocation?: any,
  providedEventId?: string
) => {
  const { connectGraphQLApi } = useGraphQLAuth();
  const { setToast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const isEditing = !!editingLocation;

  const formik = useFormik({
    initialValues: initInputsValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        const input: any = {
          name: values.name.trim(),
          description: values.description?.trim() || undefined,
          capacity:
            values.capacity !== "" && values.capacity !== undefined
              ? Number(values.capacity)
              : undefined,
          isActive: values.isActive,
        };

        if (isEditing) {
          await connectGraphQLApi({
            query: `
              mutation BackofficeUpdateLocation($locationId: ID!, $input: LocationUpdateInput!) {
                backofficeUpdateLocation(locationId: $locationId, input: $input) {
                  locationId
                }
              }
            `,
            variables: { locationId: editingLocation.locationId, input },
            errorMessage: "Erro ao atualizar local",
            useNewApi: true,
          });
          setToast({
            type: "success",
            message: "Local atualizado com sucesso!",
            isActive: true,
            timeToRemoveToast: 3000,
          });
        } else {
          await connectGraphQLApi({
            query: `
              mutation BackofficeCreateLocation($input: LocationInput!) {
                backofficeCreateLocation(input: $input) { locationId }
              }
            `,
            variables: { input: { eventId: providedEventId, ...input } },
            errorMessage: "Erro ao criar local",
            useNewApi: true,
          });
          setToast({
            type: "success",
            message: "Local criado com sucesso!",
            isActive: true,
            timeToRemoveToast: 3000,
          });
        }

        onSuccess && onSuccess();
      } catch (error) {
        console.error("Error saving location:", error);
        setToast({
          type: "error",
          message: `Erro ao ${isEditing ? "atualizar" : "criar"} local.`,
          isActive: true,
          timeToRemoveToast: 5000,
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    if (editingLocation) {
      formik.setValues({
        name: editingLocation.name || "",
        description: editingLocation.description || "",
        capacity: (editingLocation.capacity ?? "") as any,
        isActive: editingLocation.isActive ?? true,
      });
    }
  }, [editingLocation]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    formik.handleChange(e);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    formik.handleSubmit();
  };

  return {
    handleInputChange,
    inputsValues: formik.values as any,
    touched: formik.touched as any,
    errors: formik.errors as any,
    handleSubmit,
    isLoading,
    isEditing,
  };
};
